import { Injectable } from '@angular/core';
import { BullhornEntityTypes } from 'datasource-service-lib';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { BullhornSettingsService } from 'settings-lib';

import { IAttachmentSource, IAttachmentSourceEntry } from './iattachment-source';

@Injectable({ providedIn: 'root' })
export class BullhornAttachmentSource implements IAttachmentSource {
  sources: IAttachmentSourceEntry[] = [
    {
      sourceEntity: BullhornEntityTypes.Candidate,
      defaultEntity: BullhornEntityTypes.Candidate,
      optionEntities: [BullhornEntityTypes.Candidate],
    },
    {
      sourceEntity: BullhornEntityTypes.ClientCorporation,
      defaultEntity: BullhornEntityTypes.ClientCorporation,
      optionEntities: [BullhornEntityTypes.ClientCorporation],
    },
    {
      sourceEntity: BullhornEntityTypes.ClientContact,
      defaultEntity: BullhornEntityTypes.ClientContact,
      optionEntities: [BullhornEntityTypes.ClientContact, `${BullhornEntityTypes.ClientContact}.${BullhornEntityTypes.ClientCorporation}`],
    },
    {
      sourceEntity: BullhornEntityTypes.Opportunity,
      defaultEntity: BullhornEntityTypes.Opportunity,
      optionEntities: [
        BullhornEntityTypes.Opportunity,
        `${BullhornEntityTypes.Opportunity}.${BullhornEntityTypes.ClientCorporation}`,
        `${BullhornEntityTypes.Opportunity}.${BullhornEntityTypes.ClientContact}`,
      ],
    },
    {
      sourceEntity: BullhornEntityTypes.Placement,
      defaultEntity: BullhornEntityTypes.Placement,
      optionEntities: [
        BullhornEntityTypes.Placement,
        `${BullhornEntityTypes.Placement}.${BullhornEntityTypes.Candidate}`,
        `${BullhornEntityTypes.Placement}.${BullhornEntityTypes.ClientCorporation}`,
        `${BullhornEntityTypes.Placement}.${BullhornEntityTypes.ClientContact}`,
        `${BullhornEntityTypes.Placement}.${BullhornEntityTypes.JobOrder}`,
      ],
    },
    {
      sourceEntity: BullhornEntityTypes.JobSubmission,
      defaultEntity: `${BullhornEntityTypes.JobSubmission}.${BullhornEntityTypes.Candidate}`,
      optionEntities: [
        `${BullhornEntityTypes.JobSubmission}.${BullhornEntityTypes.Candidate}`,
        `${BullhornEntityTypes.JobSubmission}.${BullhornEntityTypes.JobOrder}.${BullhornEntityTypes.ClientCorporation}`,
        `${BullhornEntityTypes.JobSubmission}.${BullhornEntityTypes.JobOrder}.${BullhornEntityTypes.ClientContact}`,
        `${BullhornEntityTypes.JobSubmission}.${BullhornEntityTypes.JobOrder}`,
      ],
    },
    {
      sourceEntity: BullhornEntityTypes.JobOrder,
      defaultEntity: BullhornEntityTypes.JobOrder,
      optionEntities: [
        BullhornEntityTypes.JobOrder,
        `${BullhornEntityTypes.JobOrder}.${BullhornEntityTypes.ClientCorporation}`,
        `${BullhornEntityTypes.JobOrder}.${BullhornEntityTypes.ClientContact}`,
        `${BullhornEntityTypes.JobOrder}.${BullhornEntityTypes.Opportunity}`,
      ],
    },
  ];

  constructor(private bullhornSettingsService: BullhornSettingsService) {}

  getDefault(entity: string): string {
    return this.find(entity).defaultEntity;
  }

  getOptions(entity: string): Observable<string[]> {
    return this.filterOpportunities(this.find(entity)).pipe(map((value) => value?.optionEntities));
  }

  private find(entity: string): IAttachmentSourceEntry {
    const source = this.sources.find((s) => s.sourceEntity === entity);
    if (!source) {
      throw new Error(`No source found for entity ${entity}`);
    }
    return source;
  }

  private filterOpportunities(entry: IAttachmentSourceEntry): Observable<IAttachmentSourceEntry> {
    if (entry.sourceEntity !== BullhornEntityTypes.JobOrder) {
      return of(entry);
    }

    return this.bullhornSettingsService.clientLeadOpportunityEnabled.pipe(
      map((enabled) => {
        entry.optionEntities = enabled
          ? entry.optionEntities
          : entry.optionEntities.filter((path) => !path.includes(BullhornEntityTypes.Opportunity));
        return entry;
      })
    );
  }
}
